import React, { useState, useCallback, useEffect } from 'react';
import { Flex, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fieldDefaultProps } from 'utils/fieldConfig';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

interface SearchBarProps {
  placeholder?: string;
  buttonText?: string;
  onSearch: (value: string) => void;
  className?: string;
  defaultValue?: string;
  required?: boolean;
  disabled?: boolean;
  debounceSearch?: boolean;
  debounceMs?: number;
  minChars?: number;
  resetKey?: string | number;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  buttonText,
  onSearch,
  className = '',
  defaultValue = '',
  required = true,
  disabled = true,
  debounceSearch = false,
  debounceMs = 500,
  minChars = 3,
  resetKey,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState(defaultValue);

  useEffect(() => {
    setSearchText(defaultValue);
  }, [resetKey, defaultValue]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      onSearch(value);
    }, debounceMs),
    [onSearch, debounceMs],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceSearch) {
      if (value.length >= minChars) {
        debouncedSearch(value);
      } else {
        // Clear search when input is less than minChars
        onSearch('');
      }
    }
  };

  const handleSearch = (value: string) => {
    if (value.length >= minChars) {
      onSearch(value);
    } else {
      // Clear search when input is less than minChars
      onSearch('');
    }
  };

  return (
    <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
      <Flex gap="small" className={`w-full mb-8 ${className}`}>
        <Input.Search
          placeholder={placeholder ?? t('dashboard.input.placeholder')}
          value={searchText}
          {...fieldDefaultProps}
          onChange={handleChange}
          required={required}
          onSearch={handleSearch}
          className={`flex-1 ${className}`}
          allowClear
          enterButton={
            <span className="flex items-center justify-center gap-2">
              <SearchOutlined />
              {buttonText ?? t('dashboard.input.btn')}
            </span>
          }
        />
      </Flex>
    </div>
  );
};

export default SearchBar;
